import { useEffect, useState } from 'react';
import { getAdminPebbleEnvelope } from '../../api/client';
import type { AdminPebbleEnvelope } from '../../api/types';

interface Props {
  pebbleId: string;
  /** Optional title shown above the envelope. Defaults to "Pebble envelope". */
  title?: string;
}

export default function PebbleEnvelopeView({ pebbleId, title = 'Pebble envelope' }: Props) {
  const [envelope, setEnvelope] = useState<AdminPebbleEnvelope | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAdminPebbleEnvelope(pebbleId)
      .then((env) => {
        if (!cancelled) setEnvelope(env);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pebbleId]);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-gray-800">{title}</h3>
        <span className="text-xs text-gray-500 font-mono">{pebbleId}</span>
      </div>

      {loading && <div className="text-sm text-gray-500">Loading…</div>}
      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      )}

      {envelope && (
        <div className="space-y-3 text-sm">
          {/* Story */}
          <div>
            <div className="text-xs uppercase text-gray-500 mb-1">Story</div>
            {envelope.story ? (
              <div className="flex items-center gap-3">
                <code className="text-xs text-gray-700">{envelope.story.id}</code>
                <Badge tone={envelope.story.embeddings_ready ? 'green' : 'amber'}>
                  embed {envelope.story.embeddings_ready ? 'ready' : 'pending'}
                </Badge>
                <Badge tone="gray">rev {envelope.story.revision_count}</Badge>
              </div>
            ) : (
              <span className="text-gray-400">none</span>
            )}
          </div>

          {/* Active match */}
          <div>
            <div className="text-xs uppercase text-gray-500 mb-1">Active match</div>
            {envelope.active_match ? (
              <div className="flex items-center gap-3 flex-wrap">
                <code className="text-xs text-gray-700">{envelope.active_match.match_id}</code>
                <span className="text-gray-500">↔</span>
                <code className="text-xs text-gray-700">
                  {envelope.active_match.partner_pebble_id ?? '—'}
                </code>
                <Badge tone={bandTone(envelope.active_match.band)}>
                  {envelope.active_match.band} · {envelope.active_match.score.toFixed(2)}
                </Badge>
              </div>
            ) : (
              <span className="text-gray-400">none</span>
            )}
          </div>

          {/* Refinement session */}
          <div>
            <div className="text-xs uppercase text-gray-500 mb-1">Refinement session</div>
            {envelope.refinement_session ? (
              <div className="flex items-center gap-3 flex-wrap">
                <code className="text-xs text-gray-700">{envelope.refinement_session.id}</code>
                <Badge tone={statusTone(envelope.refinement_session.status)}>
                  {envelope.refinement_session.status}
                </Badge>
                <span className="text-xs text-gray-600">
                  attempt {envelope.refinement_session.attempt}/
                  {envelope.refinement_session.maxAttempts}
                </span>
                {envelope.refinement_session.lastScore != null && (
                  <span className="text-xs text-gray-600">
                    last {envelope.refinement_session.lastScore.toFixed(2)}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-gray-400">none</span>
            )}
          </div>

          {/* Consent */}
          <div>
            <div className="text-xs uppercase text-gray-500 mb-1">Consent</div>
            {envelope.consent ? (
              <div className="flex items-center gap-3">
                <span>mine: <strong>{envelope.consent.mine}</strong></span>
                <span>theirs: <strong>{envelope.consent.theirs}</strong></span>
              </div>
            ) : (
              <span className="text-gray-400">not tracked yet</span>
            )}
          </div>

          {/* Raw JSON toggle */}
          <button
            type="button"
            className="text-xs text-pebbl-600 hover:text-pebbl-800"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? '− hide raw JSON' : '+ show raw JSON'}
          </button>
          {expanded && (
            <pre className="mt-2 text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto">
              {JSON.stringify(envelope, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function Badge({ children, tone }: { children: React.ReactNode; tone: 'green' | 'amber' | 'red' | 'gray' }) {
  const cls = {
    green: 'bg-green-100 text-green-800',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-700',
  }[tone];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {children}
    </span>
  );
}

function bandTone(band: 'fail' | 'refinement' | 'consent'): 'red' | 'amber' | 'green' {
  if (band === 'fail') return 'red';
  if (band === 'refinement') return 'amber';
  return 'green';
}

function statusTone(status: string): 'green' | 'amber' | 'red' | 'gray' {
  if (status === 'escalated_consent') return 'green';
  if (status === 'failed') return 'red';
  if (status === 'cancelled') return 'gray';
  return 'amber';
}
